import { ReactElement } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { Procedure } from "@/services/backend/procedures/procedure/procedure";
import { Progress } from "@/shared/components/ui/progress";
import { useListResultsQuery } from "@/services/backend/differentiation/service";
import { useGetSampleSetQuery } from "@/services/backend/sample-set/service";
import { cn } from "@/shared/lib/utils";
import EvaluableBadge from "@/shared/components/domain/samples/evaluable-badge";
import { EvaluableType } from "@/services/backend/sample-error/sample-error";
import {
  checkIfNotEvaluable,
  checkIfThresholdIsExceeded,
  getDifferentiatedCount,
  getMouldCount,
} from "./mould-count";

interface MouldResultProps {
  procedure: Procedure;
  className?: string;
}

function MouldResult({
  procedure,
  className = "",
}: MouldResultProps): ReactElement {
  const { data: results, error: resultError } = useListResultsQuery({
    sampleSetId: {
      active: true,
      values: [procedure.sampleSetId],
    },
    limit: 0,
    offset: 0,
  });
  const { data: sampleSet } = useGetSampleSetQuery({
    id: procedure.sampleSetId,
  });
  const mouldCount = getMouldCount(procedure);

  if (resultError) {
    return <RTKQueryErrorAlert error={resultError} />;
  }

  if (sampleSet && !sampleSet.hasDifferentiation) {
    return <div>Ohne Differenzierung</div>;
  }

  if (checkIfNotEvaluable(procedure)) {
    return <EvaluableBadge evaluableType={EvaluableType.NotEvaluable} />;
  }

  if (checkIfThresholdIsExceeded(procedure)) {
    return <div>{">100"}</div>;
  }

  if (mouldCount === 0) {
    return <div>Es liegen keine Pilze vor</div>;
  }

  if (results != null) {
    const differentiatedCount = getDifferentiatedCount(results?.items);
    return (
      <div className={cn("flex w-1/2 flex-col", className)}>
        {`${differentiatedCount} / ${mouldCount}`}
        <Progress
          indicatorClassName={
            differentiatedCount > mouldCount ? "bg-red-500" : undefined
          }
          className={cn("h-2")}
          value={(differentiatedCount / mouldCount) * 100}
        />
      </div>
    );
  }

  return <div>Keine Daten gefunden</div>;
}

export default MouldResult;
